import React, { useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { ReturnReasonModal } from "../modals/ReturnReasonModal";
import { findOfferCode } from "../../_redux/order/orderCrud";
import { OrderPaymentStripe } from "../../order-canva/OrderPaymentStripe";
import { transformDataToAddProduct } from "../NewOrderStripeUtils";
import SnackBarTool from "../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
/**
 * Action bar for the Stripe new-order summary: applies promotional and default
 * coupons, opens the send-quote canvas, and triggers order placement.
 *
 * @param {object} props - Component props.
 * @param {object} props.state - Shared new-order state from reducerStripe.
 * @param {Function} props.dispatchReducer - Dispatcher for that reducer.
 * @param {object} props.orderSummaryListStripe - Product/charge catalogue for the franchise.
 * @param {Function} props.setSendQuoteCanva - Opens/closes the send-quote canvas.
 * @param {object} props.sendQuoteCanva - Current send-quote canvas state.
 * @returns {React.ReactElement} The summary action bar.
 */
export const OrderSummaryButton = ({
  state,
  dispatchReducer,
  orderSummaryListStripe,
  setSendQuoteCanva,
  sendQuoteCanva,
}) => {
  const [promoCodeText, setPromoCodeText] = useState(null);
  const [openCanvaQuote, setOpenCanvaQuote] = useState({ open: false });
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });

  /**
   * Bumped whenever the coupon list is cleared. A lookup captures the value at
   * request time and discards its response if it has moved on, so a coupon
   * resolved before a reset cannot reappear after it.
   */
  const promoGenerationRef = useRef(0);

  /**
   * Look up a coupon code and append it to the order's applied coupons.
   *
   * @param {string} applyCode - Coupon id/code to resolve.
   * @param {string|number} franchise - Franchise id for the lookup.
   * @param {boolean} [skipDuplicateCheck=false] - Skip the "already applied"
   *   guard. Set by applyDefaultCoupon, which resets the list to [] before this
   *   call: the captured `state.promoCoupon` still holds the pre-reset coupons,
   *   so the guard would false-positive and abort the re-apply. The
   *   ADD_PROMO_COUPON reducer still de-dupes against the fresh list.
   */
  const handleOfferCode = (applyCode, franchise, skipDuplicateCheck = false) => {
    setSnack({ call: false, type: "", message: "" });
    const arr = [...(state?.promoCoupon ?? [])];
    const checkExists = arr.some((e) => e.id === applyCode);
    const generation = promoGenerationRef.current;
    if (!skipDuplicateCheck && checkExists) {
      setSnack({
        call: true,
        type: "error",
        message: "Coupon already applied",
      });
      return;
    }
    findOfferCode(applyCode, franchise)
      .then((res) => {
        // The coupon list was cleared while this lookup was in flight, so this
        // response belongs to a superseded state — drop it.
        if (generation !== promoGenerationRef.current) {
          return;
        }

        const resolvedCoupon =
          res.data.data.percent_off !== null
            ? {
                type: "percent",
                id: res.data.data.id,
                value: res.data.data.percent_off,
                name: applyCode,
                isDeletable: true,
              }
            : {
                type: "fixed",
                id: res.data.data.id,
                value: +res.data.data.amount_off / 100,
                name: applyCode,
                isDeletable: true,
              };
        // if (res.data.data[0].coupon.discount_type === "percentage") {
        //     const val = res.data.data[0].coupon;
        //     const check =
        //         val?.item_constraints?.some(
        //             (e) => e.item_type === "plan" && e.constraint === "all"
        //         ) || false;
        //     if (val?.apply_on === "each_specified_item" && check) {
        //         arr.push({
        //             type: "percent",
        //             id: res.data.data[0].coupon.id,
        //             value: res.data.data[0].coupon.discount_percentage,
        //             name: applyCode,
        //             check: "billing",
        //             isDeletable: true,
        //         });
        //     } else {
        //         arr.push({
        //             type: "percent",
        //             id: res.data.data[0].coupon.id,
        //             value: res.data.data[0].coupon.discount_percentage,
        //             name: applyCode,
        //             isDeletable: true,
        //         });
        //     }
        // } else {
        //     const val = res.data.data[0].coupon;
        //     const check =
        //         val?.item_constraints?.some(
        //             (e) => e.item_type === "plan" && e.constraint === "all"
        //         ) || false;
        //     if (val?.apply_on === "each_specified_item" && check) {
        //         arr.push({
        //             type: "fixed",
        //             id: res.data.data[0].coupon.id,
        //             value: res.data.data[0].coupon.discount_amount,
        //             name: applyCode,
        //             check: "billing",
        //             isDeletable: true,
        //         });
        //     } else {
        //         arr.push({
        //             type: "fixed",
        //             id: res.data.data[0].coupon.id,
        //             value: res.data.data[0].coupon.discount_amount,
        //             name: applyCode,
        //             isDeletable: true,
        //         });
        //     }
        // }
        // Append against whatever the reducer holds now, rather than replacing
        // the list with the snapshot taken before this lookup was sent — two
        // coupons applied in quick succession would otherwise lose each other.
        dispatchReducer({
          type: "ADD_PROMO_COUPON",
          value: resolvedCoupon,
        });
        // setSnack({
        //   call: true,
        //   type: "success",
        //   message: res.data.message,
        // });
        setPromoCodeText("");
      })
      .catch((error) => {
        // setSnack({
        //   call: true,
        //   type: "error",
        //   message: error?.response?.data?.message,
        //   error: error?.response?.data?.errors,
        // });
      });
  };

 /** Replace any applied coupons with the chosen default coupon. */
 const applyDefaultCoupon = (coupon) => {
    // Invalidate anything already in flight before clearing, so an earlier
    // lookup cannot land afterwards and re-add its coupon to the fresh list.
    promoGenerationRef.current += 1;

    dispatchReducer({
      type: "SET_PROMO_COUPON",
      value: [], // reset first
    });

    // The list was just reset to [] above, but the captured state still shows
    // the old coupons; skip the stale duplicate guard so re-selecting an applied
    // default coupon still re-runs the lookup. ADD_PROMO_COUPON de-dupes anyway.
    handleOfferCode(coupon.id, state?.franchise_id, true);
  };

/** Renders the fixed £10/£20/£30-off shortcut buttons, each applying its coupon as the default. */
const CouponList = () => {
  const coupons = [
    { id: "10_OFF", label: "£10.00 Off", type: "once" },
    { id: "20OFF", label: "£20.00 Off", type: "once" },
    { id: "30OFF", label: "£30.00 Off", type: "once" },
  ];

    return (
      <>
      <label className="w-full font-medium mb-2 p-1">Available Coupons:</label>
      <div className="flex flex-wrap">
        {coupons.map((coupon) => (
          <button
            onClick={() => applyDefaultCoupon(coupon)}
            key={coupon.id}
            className="btn btn-primary font-weight-bolder font-size-sm m-2 rounded-full text-sm font-medium shadow-sm 
                      border hover:shadow-md transition"
          >
            {coupon.id} – {coupon.label}
          </button>
        ))}
      </div>
      </>
    );
}


  /** Adds the ad-hoc charge product to the cart, priced from the entered ad-hoc amount. */
  const handleAddAdHoc = () => {
    const adhocCharge = orderSummaryListStripe.allProducts?.find(
      (e) => e.stripe_product_id === import.meta.env.REACT_APP_PRODUCT_ADHOC_CHARGE
    );
    const data = transformDataToAddProduct(adhocCharge);
    data.cost = state?.adhocGbpDummy;
    data.total = state?.adhocGbpDummy;
    const arr = [...state.product_array, data];
    dispatchReducer({
      type: "SET_DATA",
      data: { ...state, product_array: arr, set: 10 },
    });
  };
  return (
    <>
      <div id="btn-neworderpage">
        <div className="row">
          <div className="col-4">
            <input
              type="text"
              // pattern="[0-9]{5}"
              // maxLength={"3"}
              className={`form-control  mt-2`}
              placeholder="Adhoc Charge"
              value={state?.adhocChargeName}
              onChange={(e) => {
                dispatchReducer({
                  type: "SET_DATA",
                  data: { ...state, adhocChargeName: e.target.value },
                });
              }}
            />
          </div>
          <div className="col-4">
            <input
              type="text"
              pattern="[0-9]{5}"
              maxLength={"5"}
              className={`form-control  mt-2`}
              placeholder="£ 0.00"
              value={state?.adhocGbpDummy}
              onChange={(e) => {
                if (e.target.value === "+") return;
                dispatchReducer({
                  type: "SET_DATA",
                  data: { ...state, adhocGbpDummy: e.target.value },
                });
              }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary font-weight-bolder font-size-sm mt-2  col-4 "
            onClick={() => handleAddAdHoc()}
            disabled={
              state?.adhocGbpDummy && state?.adhocGbpDummy != "" ? false : true
            }
          >
            {/* {intl.formatMessage({ id: "Add.Charge" })} */}
            Add Charge
          </button>
          <div className="col-4">
            <input
              type="text"
              className={`form-control  mt-3 `}
              placeholder={`Offer Code`}
              name="offerCode"
              value={promoCodeText}
              maxLength="40"
              onChange={(e) => setPromoCodeText(e.target.value)}
              //   disabled={state.productArray.some((e)=>e.id === "Howdens")}
            />
            {/* {state?.coupon_id?.some((e) => e.name === applyCode) && ( */}
            <span style={{ color: "red" }}>
              {/* {intl.formatMessage({ id: "Already.used" })} */}
              {/* Already used */}
            </span>
            {/* )} */}
          </div>
          <div className="col-4">
            <button
              type="button"
              onClick={() =>
                handleOfferCode(promoCodeText, state?.franchise_id)
              }
              className="btn btn-primary font-weight-bolder font-size-sm  mt-3 w-100"
              //   disabled={
              //     applyCode === "" ||
              //     state?.coupon_id?.some((e) => e.name === applyCode)
              //   }
              disabled={state?.promoCoupon?.length}
            >
              {/* {intl.formatMessage({ id: "Apply.Code" })} */}
              Apply Code
            </button>
            {/* {formik.touched.offerCode && formik.errors.offerCode ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.offerCode}</div>
              </div>
            ) : null} */}
          </div>
          <Dropdown
            className="col-4 dropdown-inline"
            style={{ padding: 0 }}
            drop="down"
            alignRight
          >
            <Dropdown.Toggle
              variant="transparent"
              className="btn btn-primary font-weight-bolder font-size-sm dropdown-toggle  mt-3 w-100 "
              id="dropdown-toggle-top2"
            >
              Action
            </Dropdown.Toggle>

            <Dropdown.Menu className="w-100">
              {console.log(
                "mohit-90",
                state,
                state?.containerTypeSelect === "container",
                !state.collectionRetrun?.collection_date &&
                  !state.siteSelect?.id,
                !state.collectionRetrun?.collection_date,
                !state.siteSelect?.id
              )}
              <Dropdown.Item
                disabled={
                  state?.containerTypeSelect === "container"
                    ? !state.collectionRetrun?.collection_date ||
                      !state.siteSelect?.id
                    : !state.collectionRetrun?.start_time_slot
                }
                onClick={() => setOpenCanvaQuote({ open: true })}
              >
                Make Payment
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => setSendQuoteCanva({ type: "quote", open: true })}
                // disabled={state.competitorPrice?.check}
              >
                {/* {intl.formatMessage({ id: "Send.Quote" })} */}
                Send Quote
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => setSendQuoteCanva({ type: "draft", open: true })}
                // disabled={state.competitorPrice?.check}
              >
                {/* {intl.formatMessage({ id: "Save.Draft" })} */}
                Save Draft
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="adhoc-charge mt-2"></div>
          <CouponList/>
      </div>
      <div className="summer-footer w-100 justify-content-end"></div>
      <ReturnReasonModal
      //   setShow={setShow} show={show}
      />
      {openCanvaQuote.open ? (
        <>
          <OrderPaymentStripe
            state={state}
            dispatchReducer={dispatchReducer}
            setOpenCanvaQuote={setOpenCanvaQuote}
            // snack={snack}
            // setSnack={setSnack}
          />
          <div
            className="offcanvas-overlay"
            onClick={() => setOpenCanvaQuote({ type: "", open: false })}
          ></div>
        </>
      ) : (
        ""
      )}
      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </>
  );
};
